/* eslint-disable @typescript-eslint/no-duplicate-enum-values */
import { get } from 'lodash-es'
import { Country } from 'react-phone-number-input'
import en from 'react-phone-number-input/locale/en.json'

import { BadgeColor } from 'components/Badge'
import { DropdownItemType } from 'components'

import { colors } from 'utils/colors'
import { LeadCallStatusId, TeamMember, ValueOf } from 'types/global'
import { User } from 'services/types/user'
import { DispositionType } from 'services/disposition'
import { getCountries } from 'react-phone-number-input/input'
import { CustomAddressFieldAccessor } from 'store/types/settings'
import { CustomFieldAccessor, TwoFactorMethod } from 'types/global'
import { CustomAddressFieldAccessorResponse } from 'services/types/settings'

export const COUNTRY_CODES: Array<Country> = getCountries()

COUNTRY_CODES.sort((a, b) => {
  const countryAName = get(en, a, null) as string
  const countryBName = get(en, b, null) as string

  if (countryAName < countryBName) {
    return -1
  }

  if (countryAName > countryBName) {
    return 1
  }

  return 0
})

export const COUNTRY_OPTIONS: DropdownItemType<string>[] = COUNTRY_CODES.map((item: Country) => ({
  label: get(en, item, null) as string,
  value: item,
}))

export const FETCHING_STATUS = Object.freeze({
  unSet: 'UN_SET' as const,
  loading: 'LOADING' as const,
  success: 'SUCCESS' as const,
  fail: 'FAIL' as const,
  updateFail: 'UPDATE_FAIL' as const,
})

export type FetchingStatus = typeof FETCHING_STATUS[keyof typeof FETCHING_STATUS]

export enum CORE_LEAD_STATUS {
  New = 1,
  Contacted = 2,
  Validated = 3,
  'In Progress' = 4,
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  Converted = 5,
  Won = 5,
  Invalid = 6,
  Lost = 7,
}

export const ADDRESS_FIELDS_ACCESSOR: { [key in CustomAddressFieldAccessorResponse]: CustomAddressFieldAccessor } = {
  address_line_1: 'addressLine1',
  address_line_2: 'addressLine2',
  city: 'city',
  country: 'country',
  postal_code: 'postalCode',
  province: 'province',
  suburb: 'suburb',
  latitude: 'lat',
  longitude: 'lng',
  ward: 'ward',
}

export const LEAD_APPOINTMENT_STATUS = {
  // Special status
  'Not Started': -1,

  // Pre-defined status
  Pending: 1,
  'In Progress': 2,
  Completed: 3,
  Canceled: 4,
  'Pending Minutes': 5,
} as const

export const LEAD_APPOINTMENT_STATUS_LABEL: Record<ValueOf<typeof LEAD_APPOINTMENT_STATUS>, string> = {
  [LEAD_APPOINTMENT_STATUS['Not Started']]: 'not_started_lbl',
  [LEAD_APPOINTMENT_STATUS.Pending]: 'pending_lbl',
  [LEAD_APPOINTMENT_STATUS['In Progress']]: 'in_progress_lbl',
  [LEAD_APPOINTMENT_STATUS.Completed]: 'completed_lbl',
  [LEAD_APPOINTMENT_STATUS.Canceled]: 'canceled_lbl',
  [LEAD_APPOINTMENT_STATUS['Pending Minutes']]: 'meeting_minute_lbl',
}

export const LEAD_TASK_STATUS = {
  Done: 'DONE',
  Open: 'OPEN',
  Pending: 'Pending',
} as const

export const LEAD_CALL_STATUS = Object.freeze({
  Answered: '1',
  'Not answered': '2',
  'Not Answered': '2',
  Unreachable: '3',
} as const)

export const LEAD_CALL_STATUS_LABEL: Record<LeadCallStatusId, string> = {
  [LEAD_CALL_STATUS.Answered]: 'answered',
  [LEAD_CALL_STATUS['Not Answered']]: 'not_answered_call_lbl',
  [LEAD_CALL_STATUS.Unreachable]: 'unreachable',
}

export const LEAD_TYPE = Object.freeze({
  individual: 1,
  corporate: 2,
})

export const LOOKUP_FIELDS = {
  ACCOUNT_STATUS: 'ACCOUNT_STATUS',
  CALLBACK_ROUTING: 'CALLBACK_ROUTING',
  CONTACT_INFO_TYPE: 'CONTACT_INFO_TYPE',
  CONTACT_TYPE: 'CONTACT_TYPE',
  CORE_STATUS_COLOR: 'CORE_STATUS_COLOR',
  INDUSTRY: 'INDUSTRY',
  LEAD_CAMPAIGN: 'LEAD_CAMPAIGN',
  LEAD_PROCESS: 'LEAD_PROCESS',
  LEAD_PROCESS_COLOR: 'LEAD_PROCESS_COLOR',
  LEAD_SOURCE: 'LEAD_SOURCE',
  LEAD_STATUS: 'LEAD_STATUS',
  LEAD_SOURCE_TYPE: 'LEAD_SOURCE_TYPE',
  LEAD_TYPE: 'LEAD_TYPE',
  USER: 'USER',
  USER_GROUP: 'USER_GROUP',
  SALES_OBJECTIVES: 'SALES_OBJECTIVES',
  QUOTATION_STATUS: 'QUOTATION_STATUS',
} as const

export const OBJECT_CONTROL_KEYS: {
  [key in
    | 'account_status'
    | 'address_field'
    | 'app_settings'
    | 'branch_settings'
    | 'contact_info_type'
    | 'contact_type'
    | 'industry'
    | 'lead_campaign'
    | 'lead_process'
    | 'lead_source'
    | 'lead_source_type'
    | 'lead_status'
    | 'lead_type'
    | 'master_settings'
    | 'translation'
    | 'user_group'
    | 'quotation_status']: keyof typeof LOOKUP_FIELDS | null
} = {
  account_status: 'ACCOUNT_STATUS',
  address_field: null,
  app_settings: null,
  branch_settings: null,
  contact_info_type: 'CONTACT_INFO_TYPE',
  contact_type: 'CONTACT_TYPE',
  industry: 'INDUSTRY',
  lead_campaign: 'LEAD_CAMPAIGN',
  lead_process: 'LEAD_PROCESS',
  lead_source: 'LEAD_SOURCE',
  lead_source_type: 'LEAD_SOURCE_TYPE',
  lead_status: 'LEAD_STATUS',
  lead_type: 'LEAD_TYPE',
  master_settings: null,
  translation: null,
  user_group: 'USER_GROUP',
  quotation_status: 'QUOTATION_STATUS',
}

export const NEXT_LEAD_ERROR_CODES = Object.freeze({
  appointmentNotClear: 'appointment_not_clear' as const,
  callTaskNotClear: 'call_task_not_clear' as const,
  dispositionNotClear: 'disposition_not_clear' as const,
  firstActivityNotClear: 'first_activity_not_clear' as const,
  noNextLead: 'no_lead_left' as const,
  unselectLiveCallOutcome: 'unselect_live_call_outcome' as const,
  taskNotClear: 'task_not_clear' as const,
  noOverdueTaskLeft: 'no_overdue_task_left' as const,
})

export enum CUSTOM_FIELDS {
  SingleLineInput = 1,
  DatePicker = 2,
  Paragraph = 3,
  SelectMultiple = 4,
  Select = 5,
  Currency = 6,
  NumberDigit = 7,
  URL = 8,
}

export enum FIELD_TYPE_ACCESSOR {
  textInput = 'text_input',
  paragraph = 'paragraph',
  datePicker = 'date_picker',
  singleSelect = 'single_select',
  multiSelect = 'multi_select',
  url = 'url',
  number = 'number',
  currency = 'currency',
  checkbox = 'checkbox',
  autoNumber = 'auto_number',
  lookup = 'lookup',
}

export const FIELD_TYPES: Array<{
  icon: string | string[]
  iconColor: keyof typeof colors
  accessor: CustomFieldAccessor
}> = [
  {
    icon: ['far', 'paragraph'],
    iconColor: 'gray5',
    accessor: FIELD_TYPE_ACCESSOR.paragraph,
  },
  {
    icon: ['far', 'calendar-alt'],
    iconColor: 'gray5',
    accessor: FIELD_TYPE_ACCESSOR.datePicker,
  },
  {
    icon: ['fa', 'check'],
    iconColor: 'gray5',
    accessor: FIELD_TYPE_ACCESSOR.singleSelect,
  },
  {
    icon: ['fal', 'list-ul'],
    iconColor: 'gray5',
    accessor: FIELD_TYPE_ACCESSOR.multiSelect,
  },
  {
    icon: ['fa', 'link'],
    iconColor: 'gray5',
    accessor: FIELD_TYPE_ACCESSOR.url,
  },
  {
    icon: ['far', 'sort-numeric-up'],
    iconColor: 'gray5',
    accessor: FIELD_TYPE_ACCESSOR.number,
  },
  {
    icon: ['far', 'font'],
    iconColor: 'gray5',
    accessor: FIELD_TYPE_ACCESSOR.textInput,
  },
  {
    icon: ['far', 'dollar-sign'],
    iconColor: 'gray5',
    accessor: FIELD_TYPE_ACCESSOR.currency,
  },
  {
    icon: ['fa', 'plus'],
    iconColor: 'gray5',
    accessor: FIELD_TYPE_ACCESSOR.autoNumber,
  },
  {
    icon: ['far', 'magnifying-glass'],
    iconColor: 'gray5',
    accessor: FIELD_TYPE_ACCESSOR.lookup,
  },
]

export const CAMS_SALES_AGENT_CODE = 'CAMS_SALES_AGENT'

export enum USER_PROFILES {
  Manager = 'Manager',
  SuperAdmin = 'Super Administrator',
  RegularUser = 'Regular User',
}

export const CAMS_FITMENT_IMPORT_STATUSES = Object.freeze({
  Completed: 'COMPLETED',
  Idle: 'IDLE',
  Importing: 'IMPORTING',
  Queued: 'QUEUED',
} as const)

export const USER_STATUS: { [key in User['status']]: TeamMember['status'] } = Object.freeze({
  ACTIVE: 'Active',
  INACTIVE: 'Deactivated',
  PENDING_VERIFICATION: 'Pending Verification',
})

export const TWO_FACTOR_METHODS = {
  email: 1,
  sms: 2,
} as const

export const TWO_FACTOR_TYPES: Record<TwoFactorMethod, TwoFactorMethod> = {
  email: 'email',
  sms: 'sms',
} as const

export const DEFAULT_TABLE_PAGE_SIZE = 100

export const BILLING_PERIOD = {
  '6m': '6 months',
  '12m': '12 months',
  '24m': '24 months',
  '36m': '36 months',
} as const

export const BILLING_CYCLE = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  biAnnually: 'Bi-Annually',
  yearly: 'Annually',
} as const

export const PRICE_STATUSES = {
  pending: 'PENDING',
  active: 'ACTIVE',
  inactive: 'INACTIVE',
  rejected: 'REJECTED',
} as const

export const DISPOSITION_TYPES: { [key: string]: DispositionType } = Object.freeze({
  success: 'SUCCESS',
  lost: 'LOST',
  stay: 'STAY',
  previous: 'PREVIOUS',
  invalid: 'INVALID',
} as const)

export const PRODUCT_ITEM_TYPES = Object.freeze({
  bundle: 'bundle',
  product: 'product',
  service: 'service',
  addon: 'add_on',
} as const)

export const QUOTATION_STATUS_LABEL = Object.freeze({
  draft: 'Draft',
  pendingForApproval: 'Pending for approval',
  autoApproved: 'Auto Approved',
  approvedByManager: 'Approved by manager',
  rejectedByManager: 'Rejected by manager',
  sentToCustomer: 'Sent to customer',
  acceptedByCustomer: 'Accepted by customer',
  rejectedByCustomer: 'Rejected by customer',
} as const)

export const QUOTATION_STATUSES: ReadonlyArray<{ label: string; color: BadgeColor }> = Object.freeze([
  {
    label: QUOTATION_STATUS_LABEL.draft,
    color: 'gray',
  },
  {
    label: QUOTATION_STATUS_LABEL.pendingForApproval,
    color: 'orange',
  },
  {
    label: QUOTATION_STATUS_LABEL.autoApproved,
    color: 'green',
  },
  {
    label: QUOTATION_STATUS_LABEL.approvedByManager,
    color: 'green',
  },
  {
    label: QUOTATION_STATUS_LABEL.rejectedByManager,
    color: 'red',
  },
  {
    label: QUOTATION_STATUS_LABEL.sentToCustomer,
    color: 'purple',
  },
  {
    label: QUOTATION_STATUS_LABEL.acceptedByCustomer,
    color: 'green',
  },
  {
    label: QUOTATION_STATUS_LABEL.rejectedByCustomer,
    color: 'red',
  },
] as const)

export const QUOTATION_VALIDITY_LABEL = Object.freeze({
  days: 'Days',
  months: 'Months',
} as const)

export const LeadViewType = Object.freeze({
  open: 'OPEN',
  converted: 'CONVERTED',
  invalid: 'INVALID',
  lost: 'LOST',
} as const)

export const ACTIVE_LEAD_TAB_STORAGE_KEY = 'lead-detail-current-tab'

export const LEAD_TAB_ID = Object.freeze({
  task: 'tasks-tab',
  activity: 'activities-tab',
  document: 'documents-tab',
  fitment: 'fitments-tab',
  opportunity: 'opportunities-tab',
  additionalInfo: 'additional-info-tab', // FIXME: remove after v1 deprecated
} as const)

export enum LeadPriority {
  high = 4,
  highest = 5,
  low = 2,
  lowest = 1,
  medium = 3,
}

export const QUOTATION_APPROVAL_LEVEL_FALLBACK = 0

export const LEAD_MEETING_MINUTE_EMAIL_STATUS = Object.freeze({
  pending: 0,
  sent: 1,
  sentWithoutEmail: 2,
} as const)

export const Z_INDEX = Object.freeze({
  notification: 100,
  tooltip: 90,
  modal: 80,
  dropdown: 70,
  panel: 65,
  window: 60,
  TodoListPopOver: 1,
  menuListPopOver: 2,
  header: 50,
  FloatingAudioPlayer: 9999,
} as const)

const minute = 1000 * 60
const hour = minute * 60

export const STALE_TIME = Object.freeze({
  default: 1000 * 5, // 5 seconds
  minute: minute,
  hour: hour,
  day: hour * 24,
  lookup: hour,
  customField: hour,
  masterSetting: hour * 24,
  branchSetting: hour,
} as const)

export enum AccessType {
  create = 'create',
  delete = 'delete',
  edit = 'edit',
  view = 'view',
}

export enum DndError {
  'file-invalid-type' = 'file_not_supported_error',
  'file-too-large' = 'file_too_big_error',
  'file-too-small' = 'file_too_small_error',
  'too-many-files' = 'too_many_files_error',
}

export const OPPORTUNITY_STATUS = {
  won: 'Won',
  lost: 'Lost',
}

export const SEND_EMAIL = 'send-email' as const
export const LOG_EMAIL = 'log-email' as const
export const QUOTATION_EMAIL = 'quotation-email' as const
export const MEETING_MINUTE_EMAIL = 'meeting-minute' as const
export const INTERNAL_NOTE_EMAIL = 'internal-note' as const
export const EMAIL_SUBJECT_LENGTH = 255 as const

export const EMAIL_STATUS = {
  failed: 'failed',
  sent: 'sent',
  error: 'error',
  pending: 'pending',
}

export const CALLBACK_ASSIGNEE_TYPES = {
  leadOwner: 'lead_owner',
  loggedInUser: 'logged_in_user',
} as const

// lead details tabs
export const LEAD_TAB = 'leadTab'
export const LEAD_DUPLICATE_TAB = 'lead-duplicate-tab'
export const LEAD_LOOK_UP_TAB = 'lead-lookup-tab'

export const LEAD_PREVIEW_ORDER_QUERY_PARAM = 'preview-layout-order'
export const TASK_POPUP_TRIGGER_WIDTH = 1200
